import React, { useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { enqueueSnackbar } from 'notistack';
import { ExportHubAPI, ChecklistTemplate } from 'api/ExportHubAPI';
import ExportDrawer from './ExportDrawer';
import { ChipInput } from './formControls';

interface Props {
  open: boolean;
  groupId: string | undefined;
  templates: ChecklistTemplate[];
  onClose: () => void;
  onChanged: () => void;
}

const ManageTemplatesDrawer: React.FC<Props> = ({
  open,
  groupId,
  templates,
  onClose,
  onChanged,
}) => {
  const [name, setName] = useState('');
  const [items, setItems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!groupId || !name.trim()) return;
    setBusy(true);
    try {
      await ExportHubAPI.createChecklistTemplate({
        group_id: groupId,
        name: name.trim(),
        items: items.map((label) => ({ label: label })),
      });
      enqueueSnackbar('Template created.', { variant: 'success' });
      setName('');
      setItems([]);
      onChanged();
    } catch (err) {
      console.error('Failed to create template:', err);
      enqueueSnackbar('Failed to create template.', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t: ChecklistTemplate) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.deleteChecklistTemplate(t.id, groupId);
      enqueueSnackbar('Template deleted.', { variant: 'success' });
      onChanged();
    } catch (err) {
      console.error('Failed to delete template:', err);
      enqueueSnackbar('Failed to delete template.', { variant: 'error' });
    }
  };

  return (
    <ExportDrawer open={open} title='Manage Checklist Templates' onClose={onClose}>
      <div className='eh-tpl-create'>
        <label className='eh-field'>
          <span className='eh-field-label'>New template name</span>
          <input
            className='eh-input'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='e.g. DSP-5 standard checklist'
            maxLength={120}
          />
        </label>
        <div className='eh-field'>
          <span className='eh-field-label'>Items</span>
          <ChipInput
            values={items}
            onChange={setItems}
            placeholder='Add an item, press Enter'
          />
        </div>
        <button
          className='btn-primary'
          type='button'
          onClick={create}
          disabled={busy || !name.trim()}
          style={{ opacity: busy || !name.trim() ? 0.6 : 1, alignSelf: 'flex-start' }}
        >
          {busy ? 'Saving…' : 'Create template'}
        </button>
      </div>

      <div className='eh-drawer-section'>Saved templates</div>
      {templates.length === 0 && (
        <div className='eh-detail-note'>No templates yet.</div>
      )}
      <div className='eh-tpl-list'>
        {templates.map((t) => (
          <div className='eh-tpl-row' key={t.id}>
            <div>
              <div className='eh-tpl-name'>{t.name}</div>
              <div className='eh-tpl-meta'>
                {t.items.length} item{t.items.length === 1 ? '' : 's'}
              </div>
            </div>
            <button
              className='eh-icon-btn-plain'
              type='button'
              onClick={() => remove(t)}
              aria-label={`Delete ${t.name}`}
            >
              <DeleteOutlineIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        ))}
      </div>
    </ExportDrawer>
  );
};

export default ManageTemplatesDrawer;
